import { createTheme } from '@mui/material/styles';
import { typography, spacing, shape, elevation, opacity, animation } from './tokens';

// Dark theme color palette
const darkColors = {
  // Primary colors
  primary: {
    main: '#90caf9', // Lighter primary for dark mode
    light: '#bbdefb',
    dark: '#42a5f5',
    contrastText: '#000',
  },
  
  // Background colors
  background: {
    default: '#212121',
    paper: '#212121',
    sidebar: '#2d2d2d',
    appBar: '#2d2d2d',
    input: '#2d2d2d',
    attachment: '#2d2d2d',
    message: {
      user: '#212121',
      assistant: '#212121',
    },
    preview: '#2d2d2d',
    menuHeader: '#2d2d2d',
    modal: '#000000cc',
    recording: '#000000b3',
    playback: '#000000b3',
    overlay: '#ffffff0d',
    badge: {
      primary: '#ffffff33',
      secondary: '#ffffff1a',
    },
    button: {
      clicked: '#90caf914',
      clickedSecondary: '#f4433614',
    },
  },
  
  // Text colors
  text: {
    primary: '#ffffffde',
    secondary: '#ffffff99',
    disabled: '#ffffff61',
    groupHeader: '#ffffff80',
    menuHeader: '#ffffff',
    menuIcon: '#ffffffb3',
  },
  
  // Border colors
  border: {
    default: '#ffffff1f',
    input: '#ffffff3b',
    focus: '#90caf9',
    divider: '#ffffff1f',
    attachment: '#ffffff26',
  },
  
  // Interactive states
  action: {
    hover: '#ffffff0a',
    selected: '#2d2d2d',
    disabled: '#ffffff42',
    focus: '#90caf91f',
  },
  
  // Status colors (adjusted for dark mode)
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    recording: '#4CAF50',
  },
  
  // Grey palette (dark mode specific)
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  
  // Component-specific colors
  chatSession: {
    background: {
      default: 'transparent',
      active: '#ffffff1a',
      hover: '#ffffff0d',
      activeHover: '#ffffff26',
    },
    text: {
      default: '#ffffffcc',
      active: '#fff',
    },
  },
  
  // Project colors (same as light, good contrast in dark mode)
  project: {
    purple: '#9c27b0',
    blue: '#2196f3',
    green: '#4caf50',
    yellow: '#ffeb3b',
    orange: '#ff9800',
    red: '#f44336',
  },
  
  // Font colors for messages (dark mode variants)
  messageFont: {
    default: 'inherit',
    black: '#000000',
    gray: '#D3D3D3',
    blue: '#ADD8E6',
    green: '#B2D8B2',
    red: '#FFB6C1',
    purple: '#D8BFD8',
  },
};

// Create dark theme with structured customizations
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...darkColors,
  },
  
  typography: {
    fontFamily: typography.fontFamily.primary,
    fontSize: 14,
    fontWeightRegular: typography.fontWeight.regular,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.semibold,
    
    // Custom typography variants
    h1: {
      fontSize: '1.8em',
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
    },
    h2: {
      fontSize: '1.6em',
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
    },
    h3: {
      fontSize: '1.4em',
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
    },
    body1: {
      fontSize: typography.fontSize.base,
      lineHeight: typography.lineHeight.normal,
    },
    body2: {
      fontSize: typography.fontSize.sm,
      lineHeight: typography.lineHeight.normal,
    },
    caption: {
      fontSize: typography.fontSize.xs,
      textTransform: 'uppercase',
      letterSpacing: typography.letterSpacing.wide,
      fontWeight: typography.fontWeight.medium,
    },
  },
  
  shape: {
    borderRadius: shape.borderRadius.base,
  },
  
  spacing: 8, // Base spacing unit
  
  // Custom theme additions for our app
  custom: {
    elevation: elevation,
    opacity: opacity,
    animation: animation,
    colors: darkColors,
  },
  
  // Component overrides
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: darkColors.background.default,
          color: darkColors.text.primary,
        },
      },
    },
    
    // Button customizations
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          borderRadius: shape.borderRadius.md,
          transition: `all ${animation.duration.fast} ${animation.easing.ease}`,
        },
      },
    },
    
    // Paper customizations
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient
          backgroundColor: darkColors.background.paper,
        },
      },
    },
    
    // Drawer customizations for sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: darkColors.background.sidebar,
          borderRight: `1px solid ${darkColors.border.divider}`,
          boxShadow: '0px 0px 10px #00000080',
        },
      },
    },
  },
});

export default darkTheme; 